import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, X, Send, Sparkles, Mic, MicOff, Loader2, Square, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get auth token from localStorage
const getAuthToken = () => localStorage.getItem('sehatSetu_token');

const AayuAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // New: expanded/fullscreen mode
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [currentMember, setCurrentMember] = useState(null); // Track selected family member
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // Generate session ID on mount
  useEffect(() => {
    setSessionId(`aayu_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  }, []);
  
  // Watch for family member changes from localStorage
  useEffect(() => {
    const checkMember = () => {
      const savedMember = localStorage.getItem('selectedFamilyMember');
      if (savedMember) {
        try {
          const parsed = JSON.parse(savedMember);
          setCurrentMember(parsed);
        } catch (e) {
          setCurrentMember(null);
        }
      } else {
        setCurrentMember(null);
      }
    };
    
    checkMember();
    // Check periodically for changes
    const interval = setInterval(checkMember, 2000);
    return () => clearInterval(interval);
  }, []);
  
  // Auto-focus input when Aayu opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);
  
  // Check for speech recognition and audio recording support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    // Check if audio recording is supported (works on most modern browsers including iOS)
    const hasMediaRecorder = typeof MediaRecorder !== 'undefined';
    
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // On iOS, we'll use audio recording instead of Web Speech API
    if (hasMediaRecorder) {
      setSpeechSupported(true);
    } else if (SpeechRecognition && !isIOS) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = language === 'hi' ? 'hi-IN' : 'en-IN';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        setIsListening(false);
        setTimeout(() => {
          document.getElementById('aayu-send-btn')?.click();
        }, 300);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error(language === 'hi' ? 'माइक्रोफोन की अनुमति दें' : 'Please allow microphone access');
        } else {
          toast.error(language === 'hi' ? 'आवाज़ नहीं सुनाई दी' : 'Could not hear you');
        }
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [language]);
  
  // Update recognition language when language changes
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = language === 'hi' ? 'hi-IN' : 'en-IN';
    }
  }, [language]);
  
  // Get supported audio MIME type for recording - prioritize Whisper-compatible formats
  const getSupportedMimeType = () => {
    // Whisper-compatible formats in order of preference
    // IMPORTANT: audio/webm;codecs=opus is rejected by Whisper despite webm being "supported"
    const whisperCompatibleTypes = [
      'audio/mp4',              // iOS Safari 14.3+ with AAC - Whisper accepts
      'audio/mp4;codecs=aac',   // Explicit AAC codec
      'audio/wav',              // PCM/WAV - Whisper accepts
      'audio/mpeg',             // MP3 - Whisper accepts
      'audio/webm',             // Generic webm (sometimes works)
    ];
    
    for (const type of whisperCompatibleTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        console.log('Selected audio format for Whisper:', type);
        return type;
      }
    }
    
    console.log('No Whisper-compatible MIME type found, using browser default');
    return ''; // Let browser choose default
  };
  
  // Get file extension from MIME type - critical for Whisper to detect format
  const getFileExtension = (mimeType) => {
    if (mimeType.includes('mp4') || mimeType.includes('aac')) return 'm4a';
    if (mimeType.includes('wav')) return 'wav';
    if (mimeType.includes('mpeg') || mimeType.includes('mp3')) return 'mp3';
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('ogg')) return 'ogg';
    return 'm4a'; // Default - Whisper accepts m4a
  };
  
  // Start audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Don't specify mimeType - let the browser choose the best format
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      
      console.log('MediaRecorder created, mimeType:', recorder.mimeType);
      
      recorder.ondataavailable = (event) => {
        console.log('Data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        toast.error(language === 'hi' ? 'रिकॉर्डिंग में गड़बड़ी' : 'Recording error');
        setIsRecording(false);
      };
      
      recorder.onstop = async () => {
        console.log('Recording stopped, chunks:', audioChunksRef.current.length);
        
        // Get the actual MIME type used
        const actualMimeType = recorder.mimeType || 'audio/mp4';
        console.log('Using MIME type:', actualMimeType);
        
        // Create blob from chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        console.log('Audio blob created:', audioBlob.size, 'bytes');
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        if (audioBlob.size === 0) {
          toast.error(language === 'hi' ? 'कोई ऑडियो नहीं मिला' : 'No audio captured');
          setIsProcessing(false);
          return;
        }
        
        // Determine file extension based on MIME type
        let extension = 'mp4';
        if (actualMimeType.includes('webm')) extension = 'webm';
        else if (actualMimeType.includes('ogg')) extension = 'ogg';
        else if (actualMimeType.includes('wav')) extension = 'wav';
        else if (actualMimeType.includes('mp4') || actualMimeType.includes('m4a')) extension = 'm4a';
        
        await processAudioRecording(audioBlob, extension);
      };
      
      // Start recording - collect data when stopped
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Timer for recording duration
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 30) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
      toast.info(language === 'hi' ? '🎤 बोलिए... (30 सेकंड तक)' : '🎤 Recording... (up to 30 sec)');
    } catch (err) {
      console.error('Recording start error:', err);
      toast.error(language === 'hi' ? 'माइक्रोफोन की अनुमति दें' : 'Please allow microphone access');
    }
  };
  
  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };
  
  // Process audio and send to Whisper API for transcription
  const processAudioRecording = async (audioBlob, extension = 'mp4') => {
    setIsProcessing(true);
    
    console.log('Processing audio:', { size: audioBlob.size, type: audioBlob.type, extension });
    
    if (!audioBlob || audioBlob.size === 0) {
      toast.error(language === 'hi' ? 'कोई ऑडियो रिकॉर्ड नहीं हुआ' : 'No audio recorded');
      setIsProcessing(false);
      return;
    }
    
    try {
      const token = getAuthToken();
      
      if (!token) {
        toast.error(language === 'hi' ? 'कृपया फिर से लॉगिन करें' : 'Please login again');
        setIsProcessing(false);
        return;
      }
      
      // Send audio to backend for transcription with correct filename
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording.${extension}`);
      formData.append('language', language);
      
      console.log('Sending audio to backend...', `${API_URL}/aayu/transcribe`);
      
      const response = await axios.post(`${API_URL}/aayu/transcribe`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000  // 60 second timeout for transcription
      });
      
      console.log('Transcription response:', response.data);
      
      const transcribedText = response.data.text;
      
      if (transcribedText && transcribedText.trim()) {
        setInputValue(transcribedText);
        // Auto-send after getting transcription
        setMessages(prev => [...prev, { type: 'user', text: transcribedText }]);
        setInputValue('');
        
        // Send to Aayu
        const aayuResponse = await axios.post(`${API_URL}/aayu/chat`, {
          message: transcribedText,
          language: language,
          session_id: sessionId,
          family_member_id: currentMember && !currentMember.is_self ? currentMember.id : null,
          family_member_name: currentMember && !currentMember.is_self ? currentMember.name : null
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const { response: botResponse, action, suggestions } = aayuResponse.data;
        let msgType = 'info';
        if (action?.result?.success) msgType = 'success';
        addBotMessage(botResponse, msgType, suggestions);
        executeAction(action);
      } else {
        toast.error(language === 'hi' ? 'कुछ सुनाई नहीं दिया' : 'Could not understand audio');
      }
    } catch (err) {
      console.error('Transcription error:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
      toast.error(language === 'hi' ? `आवाज़ प्रोसेस नहीं हो पाई: ${errorMsg}` : `Could not process audio: ${errorMsg}`);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const toggleListening = () => {
    // Voice recording temporarily disabled - use text input instead
    toast.info(language === 'hi' 
      ? 'कृपया टेक्स्ट में टाइप करें। वॉइस जल्द आ रहा है!' 
      : 'Please type your message. Voice coming soon!');
  };
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Greeting when opened - with health insights
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      fetchHealthInsightGreeting();
    }
  }, [isOpen, language, messages.length]);

  const fetchHealthInsightGreeting = async () => {
    try {
      const token = localStorage.getItem('sehatSetu_token');
      const response = await axios.get(`${API_URL}/health/diary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const entries = response.data || [];
      const bpData = entries
        .filter(e => e.entry_type === 'blood_pressure')
        .sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date));
      
      let insightText = '';
      
      if (bpData.length >= 2) {
        const latest = bpData[0]?.data;
        const previous = bpData[1]?.data;
        
        if (latest && previous) {
          const diff = latest.systolic - previous.systolic;
          if (diff < -3) {
            insightText = language === 'hi' 
              ? `\n\n🎉 बधाई हो! आपका BP बेहतर हो रहा है (${latest.systolic}/${latest.diastolic})। ऐसे ही जारी रखें!`
              : `\n\n🎉 Great news! Your BP is improving (${latest.systolic}/${latest.diastolic}). Keep up the good work!`;
          } else if (diff > 5) {
            insightText = language === 'hi'
              ? `\n\n⚠️ ध्यान दें: आपका BP थोड़ा बढ़ा है (${latest.systolic}/${latest.diastolic})। नमक कम करें और आराम करें।`
              : `\n\n⚠️ Heads up: Your BP is slightly elevated (${latest.systolic}/${latest.diastolic}). Try reducing salt and managing stress.`;
          } else {
            insightText = language === 'hi'
              ? `\n\n✅ आपका BP स्थिर है (${latest.systolic}/${latest.diastolic})। बहुत अच्छे!`
              : `\n\n✅ Your BP is stable (${latest.systolic}/${latest.diastolic}). Well done!`;
          }
        }
      }
      
      const greeting = {
        type: 'bot',
        text: language === 'hi' 
          ? `नमस्ते! मैं आयु हूं - आपका अपना हेल्थ साथी। 💚${insightText}\n\nबताइए, आज कैसा महसूस कर रहे हैं?`
          : `Hi! I'm Aayu - your personal health companion. 💚${insightText}\n\nHow are you feeling today?`,
        suggestions: language === 'hi' 
          ? ['आज मेरा BP 130/85 है', 'मेरी health कैसी है?', 'इस हफ्ते का trend दिखाओ']
          : ['My BP is 130/85 today', 'How is my health looking?', 'Show my trends this week']
      };
      setMessages([greeting]);
    } catch (error) {
      // Fallback to simple greeting
      const greeting = {
        type: 'bot',
        text: language === 'hi' 
          ? 'नमस्ते! मैं आयु हूं - आपका अपना हेल्थ साथी। 💚\n\nबताइए, आज कैसा महसूस कर रहे हैं?'
          : "Hi! I'm Aayu - your personal health companion. 💚\n\nHow are you feeling today?",
        suggestions: language === 'hi' 
          ? ['आज मेरा BP 130/85 है', 'मेरी health कैसी है?', 'इस हफ्ते का trend दिखाओ']
          : ['My BP is 130/85 today', 'How is my health looking?', 'Show my trends this week']
      };
      setMessages([greeting]);
    }
  };
  
  const addBotMessage = (text, type = 'info', suggestions = null) => {
    setMessages(prev => [...prev, { type: 'bot', text, msgType: type, suggestions }]);
  };
  
  const executeAction = async (action) => {
    if (!action || action.type === 'NONE') return;
    
    const { type, data, result } = action;
    
    switch (type) {
      case 'NAVIGATE':
        if (data?.target) {
          setTimeout(() => navigate(data.target), 500);
        }
        break;
        
      case 'PRINT':
        if (data?.what === 'medicines') {
          setTimeout(() => navigate('/prescriptions?print=true'), 500);
        } else if (data?.what === 'diary') {
          setTimeout(() => navigate('/diary?print=true'), 500);
        } else if (data?.what === 'report') {
          setTimeout(() => navigate('/dashboard?print=report'), 500);
        }
        break;
        
      case 'CREATE_PDF':
        // Direct download like Export as PDF button
        const includeDiary = data?.include_diary || false;
        toast.info(language === 'hi' ? 'PDF तैयार हो रहा है...' : 'Preparing PDF...');
        
        try {
          const token = getAuthToken();
          const response = await fetch(`${API_URL}/export/pdf?include_diary=${includeDiary}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'EmergencyCard_Health_Report.pdf');
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success(language === 'hi' ? 'PDF डाउनलोड हो रहा है!' : 'PDF downloaded!');
        } catch (err) {
          console.error('PDF download error:', err);
          toast.error(language === 'hi' ? 'PDF नहीं बन पाया' : 'Could not create PDF');
        }
        break;
        
      case 'DOCTOR_QR':
        const hours = data?.hours || 24;
        setTimeout(() => navigate(`/doctor-access?hours=${hours}`), 500);
        break;
        
      default:
        // Recording actions are handled by backend
        if (result?.success) {
          toast.success(language === 'hi' ? 'सेव हो गया!' : 'Saved successfully!');
        }
        break;
    }
  };
  
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isProcessing) return;
    
    const userText = inputValue.trim();
    setMessages(prev => [...prev, { type: 'user', text: userText }]);
    setInputValue('');
    setIsProcessing(true);
    
    try {
      // Get fresh token for each request
      const token = getAuthToken();
      if (!token) {
        addBotMessage(
          language === 'hi' 
            ? 'कृपया पहले लॉगिन करें।'
            : 'Please login first to use Aayu.',
          'error'
        );
        setIsProcessing(false);
        return;
      }
      
      const response = await axios.post(`${API_URL}/aayu/chat`, {
        message: userText,
        language: language,
        session_id: sessionId,
        family_member_id: currentMember && !currentMember.is_self ? currentMember.id : null,
        family_member_name: currentMember && !currentMember.is_self ? currentMember.name : null
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const { response: botResponse, action, suggestions } = response.data;
      
      // Determine message type based on action
      let msgType = 'info';
      if (action?.type?.startsWith('RECORD_') && action?.result?.success) {
        msgType = 'success';
      } else if (action?.type === 'SET_REMINDER' && action?.result?.success) {
        msgType = 'success';
      }
      
      addBotMessage(botResponse, msgType, suggestions);
      
      // Execute the action (navigation, etc.)
      executeAction(action);
      
    } catch (error) {
      console.error('Aayu error:', error);
      
      // Check for specific error types
      let errorMsg = language === 'hi' 
        ? 'माफ करें, कुछ गड़बड़ हो गई। फिर से कोशिश करें।'
        : 'Sorry, something went wrong. Please try again.';
      
      if (error.response?.status === 401) {
        errorMsg = language === 'hi' 
          ? 'कृपया पहले लॉगिन करें।'
          : 'Please login first.';
      } else if (error.response?.status === 429) {
        errorMsg = language === 'hi' 
          ? 'बहुत सारे अनुरोध। कुछ देर रुकें।'
          : 'Too many requests. Please wait a moment.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      // Fallback message
      addBotMessage(
        errorMsg,
        'error',
        language === 'hi' 
          ? ['मेरा BP रिकॉर्ड करो', 'help', 'diary दिखाओ']
          : ['record my BP', 'help', 'show diary']
      );
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setTimeout(() => handleSendMessage(), 100);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Aayu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen ? 'bg-slate-600 rotate-90' : 'bg-gradient-to-br from-[#0D2340] to-[#0B7EA3] hover:shadow-xl'
        }`}
        data-testid="aayu-button"
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <div className="relative">
            <Sparkles className="h-6 w-6 text-white" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
          </div>
        )}
      </button>
      
      {/* Aayu Chat Window */}
      {isOpen && (
        <div 
          className={`fixed z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-fade-in transition-all duration-300 flex flex-col ${
            isExpanded 
              ? 'inset-4 sm:inset-8 md:inset-16 lg:inset-24' 
              : 'bottom-32 right-4 w-[420px] max-w-[calc(100vw-32px)] h-[500px]'
          }`}
          data-testid="aayu-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0D2340] to-[#0B7EA3] text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    Aayu
                    <span className="text-xs bg-green-500/80 px-2 py-0.5 rounded-full">
                      Intelligent
                    </span>
                  </h3>
                  <p className="text-xs text-white/70">
                    {currentMember && !currentMember.is_self 
                      ? (language === 'hi' 
                          ? `${currentMember.name} की देखभाल में मदद` 
                          : `Helping with ${currentMember.name}'s health`)
                      : (language === 'hi' ? 'आपका अपना हेल्थ साथी' : 'Your caring health companion')
                    }
                  </p>
                </div>
              </div>
              {/* Expand/Minimize button */}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title={isExpanded ? 'Minimize' : 'Expand for better reading'}
              >
                {isExpanded ? (
                  <Minimize2 className="h-5 w-5" />
                ) : (
                  <Maximize2 className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
          
          {/* Messages Area */}
          <div className={`overflow-y-auto p-4 space-y-4 bg-slate-50 ${
            isExpanded ? 'flex-1' : 'h-[350px]'
          }`} style={isExpanded ? { height: 'calc(100% - 140px)' } : {}}>
            {messages.map((msg, index) => (
              <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                  msg.type === 'user' 
                    ? 'bg-[#0D2340] text-white rounded-br-sm' 
                    : msg.msgType === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-800 rounded-bl-sm'
                      : msg.msgType === 'error'
                        ? 'bg-red-50 border border-red-200 text-red-800 rounded-bl-sm'
                        : 'bg-white shadow-sm border border-slate-100 rounded-bl-sm'
                }`}>
                  <p className={`whitespace-pre-line leading-relaxed ${isExpanded ? 'text-lg' : 'text-base'}`}>{msg.text}</p>
                  
                  {/* Suggestion chips */}
                  {msg.suggestions && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {msg.suggestions.map((sug, i) => (
                        <button
                          key={i}
                          onClick={() => handleSuggestionClick(sug)}
                          className={`bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-full transition-colors ${
                            isExpanded ? 'text-base' : 'text-sm'
                          }`}
                        >
                          {sug}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isProcessing && (
              <div className="flex justify-start">
                <div className="bg-white shadow-sm border border-slate-100 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-[#0D2340]" />
                    <span className="text-sm text-slate-500">
                      {language === 'hi' ? 'सोच रहा हूं...' : 'Thinking...'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input Area */}
          <div className="p-3 border-t border-slate-200 bg-white">
            {/* Recording indicator */}
            {isRecording && (
              <div className="flex items-center justify-center gap-2 mb-2 py-2 bg-red-50 rounded-lg">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-red-600 font-medium">
                  {language === 'hi' ? `रिकॉर्डिंग... ${recordingTime}s` : `Recording... ${recordingTime}s`}
                </span>
                <Button 
                  onClick={stopRecording} 
                  size="sm" 
                  variant="destructive"
                  className="ml-2 h-7"
                >
                  <Square className="h-3 w-3 mr-1" />
                  {language === 'hi' ? 'रोकें' : 'Stop'}
                </Button>
              </div>
            )}
            
            <div className="flex gap-3 items-center">
              {/* Microphone Button */}
              {speechSupported && (
                <Button 
                  onClick={toggleListening}
                  size="icon"
                  variant={isListening || isRecording ? "destructive" : "outline"}
                  className={`rounded-full flex-shrink-0 h-12 w-12 ${(isListening || isRecording) ? 'animate-pulse' : ''}`}
                  disabled={isProcessing}
                  data-testid="aayu-mic"
                >
                  {(isListening || isRecording) ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </Button>
              )}
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={language === 'hi' ? 'कुछ भी बोलो या लिखो...' : 'Type or speak anything...'}
                className={`flex-1 rounded-full border-slate-200 h-12 text-base px-5 ${isExpanded ? 'h-14 text-lg px-6' : ''}`}
                disabled={isProcessing || isListening || isRecording}
                data-testid="aayu-input"
              />
              <Button 
                id="aayu-send-btn"
                onClick={handleSendMessage}
                size="icon"
                className={`rounded-full bg-gradient-to-br from-[#0D2340] to-[#0B7EA3] h-12 w-12 ${isExpanded ? 'h-14 w-14' : ''}`}
                disabled={isProcessing || !inputValue.trim() || isRecording}
                data-testid="aayu-send"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
            {isListening && (
              <p className="text-xs text-center text-red-500 mt-2 animate-pulse">
                {language === 'hi' ? '🎤 सुन रहा हूं... बोलिए!' : '🎤 Listening... speak now!'}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AayuAssistant;
